import {
  Autocomplete,
  AutocompleteProps,
  Box,
  type PaperProps,
  Popper,
  type PopperProps,
  TextField,
  Tooltip,
} from "@mui/material";
import type { AutocompleteOwnerState } from "@mui/material/Autocomplete";
import { forwardRef, useId, useState } from "react";
import type { SxProps, Theme } from "@mui/material/styles";

type PaperSlotPropsObject = Partial<PaperProps> & {
  sx?: SxProps<Theme>;
};

const defaultPaperSx: SxProps<Theme> = (theme) => ({
  "& .MuiAutocomplete-option": {
    fontSize: theme.typography.caption.fontSize,
    whiteSpace: "nowrap",
  },
  "& .MuiAutocomplete-loading": {
    fontSize: theme.typography.caption.fontSize,
    whiteSpace: "nowrap",
  },
  "& .MuiAutocomplete-noOptions": {
    fontSize: theme.typography.caption.fontSize,
    whiteSpace: "nowrap",
  },
});

const visuallyHiddenSx: SxProps<Theme> = {
  border: 0,
  clip: "rect(0 0 0 0)",
  height: 1,
  margin: -1,
  overflow: "hidden",
  padding: 0,
  position: "absolute",
  whiteSpace: "nowrap",
  width: 1,
} as const;

const mergePaperSx = (existingSx?: SxProps<Theme>): SxProps<Theme> => {
  if (!existingSx) {
    return defaultPaperSx;
  }

  if (Array.isArray(existingSx)) {
    return [defaultPaperSx, ...existingSx] as SxProps<Theme>;
  }

  return [defaultPaperSx, existingSx] as SxProps<Theme>;
};

const getPaperSlotSx = (slotProps?: PaperSlotPropsObject) => slotProps?.sx;

const SmallAutocompletePopper = forwardRef<HTMLDivElement, PopperProps>(
  function SmallAutocompletePopper(props, ref) {
    const activatorWidth = props.style?.width;

    return (
      <Popper
        {...props}
        ref={ref}
        placement={props.placement ?? "bottom-start"}
        style={{
          ...props.style,
          minWidth: activatorWidth ?? undefined,
          width: "max-content",
          maxWidth: "90vw",
        }}
      />
    );
  },
);

/**
 * A small, styled Autocomplete component with pill-shaped border and contrasting background.
 * Accepts all standard MUI Autocomplete properties and can be used interchangeably with the standard Autocomplete.
 */
export function SmallAutocomplete<
  T,
  Multiple extends boolean | undefined = undefined,
  DisableClearable extends boolean | undefined = undefined,
  FreeSolo extends boolean | undefined = undefined,
>({
  error,
  helperText,
  label,
  "aria-label": ariaLabel,
  ...otherProps
}: Omit<
  AutocompleteProps<T, Multiple, DisableClearable, FreeSolo>,
  "renderInput"
> & {
  error?: boolean;
  helperText?: string;
  label?: string;
  "aria-label"?: string;
  renderInput?: AutocompleteProps<
    T,
    Multiple,
    DisableClearable,
    FreeSolo
  >["renderInput"];
}) {
  const [isOpen, setIsOpen] = useState(false);
  const helperTextId = useId();
  const hasErrorMessage = Boolean(error && helperText);
  const isErrorMessageVisible = hasErrorMessage && !isOpen;
  const paperSlotProps = otherProps.slotProps?.paper;

  const resolvedPaperSlotProps = (
    ownerState: AutocompleteOwnerState<
      T,
      Multiple,
      DisableClearable,
      FreeSolo,
      "div"
    >,
  ) => {
    const resolvedProps =
      typeof paperSlotProps === "function"
        ? (paperSlotProps(ownerState) as PaperSlotPropsObject)
        : (paperSlotProps as PaperSlotPropsObject | undefined);

    return {
      ...resolvedProps,
      sx: mergePaperSx(getPaperSlotSx(resolvedProps)),
    };
  };

  return (
    <>
      {isErrorMessageVisible && (
        <Box component="span" id={helperTextId} sx={visuallyHiddenSx}>
          {helperText}
        </Box>
      )}
      <Tooltip
        title={hasErrorMessage ? helperText : ""}
        open={isErrorMessageVisible}
        arrow
        placement="bottom-start"
      >
        <Autocomplete
          {...otherProps}
          size="small"
          onOpen={(event) => {
            setIsOpen(true);
            otherProps.onOpen?.(event);
          }}
          onClose={(event, reason) => {
            setIsOpen(false);
            otherProps.onClose?.(event, reason);
          }}
          renderInput={(params) => {
            const describedBy = [
              params.inputProps["aria-describedby"],
              isErrorMessageVisible ? helperTextId : undefined,
            ]
              .filter(Boolean)
              .join(" ");

            return (
              <TextField
                {...params}
                error={error}
                sx={(theme) => ({
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "50px",
                    backgroundColor: theme.palette.background.dark,
                    fontSize: theme.typography.caption.fontSize,
                    "& .MuiOutlinedInput-notchedOutline": {
                      border: error ? "1px solid" : "none",
                      borderColor: error ? theme.palette.error.main : "transparent",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      border: error ? "1px solid" : "none",
                      borderColor: error ? theme.palette.error.main : "transparent",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      border: error ? "1px solid" : "none",
                      borderColor: error ? theme.palette.error.main : "transparent",
                    },
                  },
                  "& .MuiAutocomplete-input": {
                    padding: `0 0 0 ${theme.spacing(0.5)} !important`,
                    fontSize: theme.typography.caption.fontSize,
                  },
                })}
                slotProps={{
                  htmlInput: {
                    ...params.inputProps,
                    "aria-describedby": describedBy || undefined,
                    "aria-errormessage": isErrorMessageVisible ? helperTextId : undefined,
                    "aria-invalid": error || undefined,
                    "aria-label": ariaLabel || label,
                  },
                }}
              />
            );
          }}
          slotProps={{
            ...otherProps.slotProps,
            paper: resolvedPaperSlotProps,
          }}
          slots={{
            ...otherProps.slots,
            popper: otherProps.slots?.popper ?? SmallAutocompletePopper,
          }}
          sx={{ minWidth: 150, ...otherProps.sx }}
        />
      </Tooltip>
    </>
  );
}
